import { UtilityConfiguration } from "@domain/project/configurations/monitoring/utility-configuration";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanProductItem } from "@domain/project/floorplan/floorplan-product-item";
import { Exclude, Expose } from "class-transformer";
import { IsString } from "class-validator";
import { v4 as uuidv4 } from "uuid";

export class FloorplanUtility extends FloorplanProductItem<UtilityConfiguration> {
  @Exclude()
  private readonly _utility: UtilityConfiguration;

  @IsString()
  @Expose({ name: "utilityId" })
  private readonly _utilityId: string;

  constructor(
    floorplan: Floorplan,
    utility: UtilityConfiguration,
    id: string,
    x: number,
    y: number,
    size: number,
    notes?: string,
  ) {
    super(floorplan, utility, id, x, y, size, notes);
    this._utilityId = utility?.id;
    this._utility = utility;
  }

  setItemTextVisibility() {
    this.itemTextVisible = this._floorplan.floorplanState.utilitiesItemTextVisible;
  }

  protected override createRawDuplicate(): ((x: number, y: number) => FloorplanUtility) | undefined {
    const config = this.config;
    const size = this.size;

    return (x: number, y: number) => FloorplanUtility.create(this._floorplan, config, x, y, size);
  }

  override getDuplicateTargetCollection() {
    return this._floorplan.utilities;
  }

  protected override registerDuplicate() {
    return (duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) =>
      this.registerDuplicateInFloorplan(
        duplicate as FloorplanUtility,
        this._floorplan.utilities,
        FloorplanEventType.UTILITY_ADDED,
        () => this._floorplan.project.setPositionNumbers(),
        afterDuplicateAdded as ((duplicate: FloorplanUtility) => void) | undefined,
      );
  }

  override delete() {
    this._floorplan.deleteUtility(this);
  }

  protected override refreshFloorplanLockState() {
    this._floorplan.floorplanState.refreshUtilitiesLock();
  }

  protected override getConfigFromProject(floorplan: Floorplan): UtilityConfiguration | undefined {
    return floorplan.getUtilityConfig(this._utilityId);
  }

  static create(floorplan: Floorplan, utility: UtilityConfiguration, x: number, y: number, size: number): FloorplanUtility {
    return new FloorplanUtility(floorplan, utility, uuidv4(), x, y, size);
  }
}
